/**
 * The dashboard's words. The server sends numbers only (console-dashboard.ts):
 * each alert is worded here from its `kind` and its numbers, each queue from
 * its name, and every link opens the console's own screen with the filters
 * the server named.
 */
import type {
  DashboardAlert,
  DashboardLink,
  DashboardQueue,
  DashboardReportSeverity,
} from "@wallyo/contracts/console/dashboard";
import { CATALOGS, t, type MessageId } from "../../i18n";
import { formatDateTime, formatNumber, formatTime } from "../../i18n/format";
import { ALL_CASES, casesHref } from "../cases/case-route";

type Tone = "ok" | "warn" | "bad";

/** The console's hash for a dashboard link. Dossiers names "late" as a tab of its own. */
export function dashboardHref(link: DashboardLink): string {
  if (link.screen === "cases") {
    return link.filters.late === "true" ? casesHref({ ...ALL_CASES, view: "late" }) : casesHref(ALL_CASES);
  }
  const query = new URLSearchParams(link.filters).toString();
  return query ? `#${link.screen}?${query}` : `#${link.screen}`;
}

/** A trade's name in the staff member's language; one newer than this build shows its key. */
export function tradeLabel(category: string): string {
  const id = `trade.${category}`;
  return Object.hasOwn(CATALOGS.fr, id) ? t(id as MessageId) : category;
}

/** "3 h 20 min" or "45 min" or "2 j": how long the oldest has waited. */
export function waitedFor(minutes: number): string {
  if (minutes < 60) return t("unit.minutes", { value: formatNumber(minutes) });
  if (minutes < 48 * 60) return t("unit.hours", { value: formatNumber(Math.floor(minutes / 60)) });
  return t("unit.days", { value: formatNumber(Math.floor(minutes / (24 * 60))) });
}

export type AlertView = { tone: Tone; tag: string; text: string; href: string | null; linkLabel: string | null };

/** An alert in words, from its kind and numbers, with where it leads. */
export function alertView(alert: DashboardAlert): AlertView {
  const tone: Tone = alert.severity === "critical" ? "bad" : "warn";
  const href = alert.link ? dashboardHref(alert.link) : null;
  const linkLabel = href ? t("dashboard.alert.open") : null;
  const base = { tone, href, linkLabel };
  switch (alert.kind) {
    case "critical_report_open":
      return {
        ...base,
        tag: t("dashboard.alert.critical_report_open.tag"),
        text: [
          t("dashboard.alert.critical_report_open.text", { count: formatNumber(alert.count) }),
          alert.suspendedPendingReview > 0 && alert.reviewBy
            ? t("dashboard.alert.critical_report_open.review", {
                count: formatNumber(alert.suspendedPendingReview),
                date: formatDateTime(alert.reviewBy),
              })
            : null,
        ]
          .filter(Boolean)
          .join(" "),
      };
    case "suspension_review_overdue":
      return {
        ...base,
        tag: t("dashboard.alert.suspension_review_overdue.tag"),
        text: t("dashboard.alert.suspension_review_overdue.text", {
          count: formatNumber(alert.count),
          date: formatDateTime(alert.oldestReviewBy),
        }),
      };
    case "cases_past_deadline":
      return {
        ...base,
        tag: t("dashboard.alert.cases_past_deadline.tag"),
        text: t("dashboard.alert.cases_past_deadline.text", {
          count: formatNumber(alert.count),
          hours: formatNumber(alert.targetHours),
        }),
      };
    case "thin_coverage":
      return {
        ...base,
        tag: t("dashboard.alert.thin_coverage.tag"),
        text: t("dashboard.alert.thin_coverage.text", {
          trade: tradeLabel(alert.category),
          city: alert.city,
          requests: formatNumber(alert.requests),
          pros: formatNumber(alert.availablePros),
        }),
      };
    case "failed_payments":
      return {
        ...base,
        tag: t("dashboard.alert.failed_payments.tag"),
        text: t("dashboard.alert.failed_payments.text", { count: formatNumber(alert.count) }),
      };
  }
}

export type QueueRow = { key: string; label: string; meta: string | null; count: number; tone: Tone; href: string };

const SEVERITIES: readonly DashboardReportSeverity[] = ["critical", "high", "medium", "low"];

/** Each queue as a row (the safety queue also gives its overdue reviews a row of their own). */
export function queueRows(queues: readonly DashboardQueue[]): QueueRow[] {
  const rows: QueueRow[] = [];
  for (const queue of queues) {
    const href = dashboardHref(queue.link);
    const busy = (bad: boolean): Tone => (queue.count === 0 ? "ok" : bad ? "bad" : "warn");
    switch (queue.queue) {
      case "late_cases":
        rows.push({
          key: queue.queue,
          label: t("dashboard.queue.late_cases"),
          meta: null,
          count: queue.count,
          tone: busy(true),
          href,
        });
        break;
      case "open_cases":
        rows.push({
          key: queue.queue,
          label: t("dashboard.queue.open_cases"),
          meta: t("dashboard.queue.open_cases.meta", { count: formatNumber(queue.unassigned) }),
          count: queue.count,
          tone: busy(false),
          href,
        });
        break;
      case "identity_verifications":
        rows.push({
          key: queue.queue,
          label: t("dashboard.queue.identity_verifications"),
          meta:
            queue.oldestWaitingMinutes === null
              ? null
              : t("dashboard.queue.identity_verifications.meta", { age: waitedFor(queue.oldestWaitingMinutes) }),
          count: queue.count,
          tone: busy(false),
          href,
        });
        break;
      case "safety_reports": {
        const bySeverity = SEVERITIES.filter((severity) => queue.bySeverity[severity] > 0)
          .map((severity) =>
            t(`dashboard.queue.severity.${severity}` as const, { count: formatNumber(queue.bySeverity[severity]) }),
          )
          .join(", ");
        rows.push({
          key: queue.queue,
          label: t("dashboard.queue.safety_reports"),
          meta: bySeverity || null,
          count: queue.count,
          tone: busy(queue.bySeverity.critical > 0),
          href,
        });
        rows.push({
          key: "overdue_suspension_reviews",
          label: t("dashboard.queue.overdue_suspension_reviews"),
          meta: t("dashboard.queue.overdue_suspension_reviews.meta"),
          count: queue.overdueSuspensionReviews,
          tone: queue.overdueSuspensionReviews > 0 ? "bad" : "ok",
          href: dashboardHref({ screen: "reports", filters: { reviewOverdue: "true" } }),
        });
        break;
      }
      case "payment_approvals":
        rows.push({
          key: queue.queue,
          label: t("dashboard.queue.payment_approvals"),
          meta: null,
          count: queue.count,
          tone: busy(false),
          href,
        });
        break;
    }
  }
  return rows;
}

/** "Chiffres lus à 10:42": when the numbers were read (up to a minute old). */
export const generatedLine = (generatedAt: string) => t("dashboard.generatedAt", { time: formatTime(generatedAt) });
